import { Fragment, useEffect } from 'react';

import BlogCategoryPage from '@/components/BasePages/BlogCategoryPage';
import BlogPage from '@/components/BasePages/BlogPage';
import DynamicRedirectPage from '@/components/BasePages/DynamicRedirectPage/DynamicRedirectPage';
import EcommStoreProductPage from '@/components/BasePages/EcommStoreProductPage/EcommStoreProductPage';
import StandardPage from '@/components/BasePages/StandardPage';
import ConstrainedExitIntent from '@/components/ExitIntent/ConstrainedExitIntent';
import {
  BlogCategoryPageContentType,
  BlogPageContent,
  PageContent,
  PageContentType,
  StandardPageContent,
} from '@/contentful';
import { EcommStoreProductPageEnhancedCF } from '@/contentful/api/types';
import useEcommMarket from '@/hooks/geo';
import { DynamicRedirectPageCF } from '@/types';

interface Props {
  content: PageContent;
}

interface IWindow extends Window {
  FS: (arg: string) => void;
}

export default function Page({
  content: {
    fields: { fullStoryEnabled, pageContent, seoFields, path, exitIntent },
  },
}: Props): React.JSX.Element | null {
  const { userLocation, setUserLocation } = useEcommMarket();

  useEffect(() => {
    const interval = setInterval(() => {
      if (fullStoryEnabled) {
        const isReady =
          typeof (window as unknown as IWindow)?.FS === 'function';

        if (isReady) {
          (window as unknown as IWindow).FS('start');
          clearInterval(interval);
        }
      }
    }, 5 * 1000);

    return () => clearInterval(interval);
  }, []);

  switch (pageContent?.sys?.contentType.sys.id) {
    case PageContentType.STANDARD:
      return (
        <Fragment>
          <StandardPage
            content={pageContent as StandardPageContent}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
          />
          {exitIntent && (
            <ConstrainedExitIntent
              exitIntentConfig={exitIntent}
              userLocation={userLocation}
            />
          )}
        </Fragment>
      );
    case PageContentType.BLOG:
      return (
        <Fragment>
          <BlogPage
            path={path}
            seoFields={seoFields}
            content={pageContent as BlogPageContent}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
          />
          {exitIntent && (
            <ConstrainedExitIntent
              exitIntentConfig={exitIntent}
              userLocation={userLocation}
            />
          )}
        </Fragment>
      );
    case PageContentType.BLOG_CATEGORY:
      return (
        <Fragment>
          <BlogCategoryPage
            content={pageContent as BlogCategoryPageContentType}
            userLocation={userLocation}
            setUserLocation={setUserLocation}
          />
          {exitIntent && (
            <ConstrainedExitIntent
              exitIntentConfig={exitIntent}
              userLocation={userLocation}
            />
          )}
        </Fragment>
      );
    case PageContentType.DYNAMIC_REDIRECT:
      return (
        <DynamicRedirectPage content={pageContent as DynamicRedirectPageCF} />
      );
    case PageContentType.ECOMM_STORE_PRODUCT:
      return (
        <EcommStoreProductPage
          content={pageContent as EcommStoreProductPageEnhancedCF}
          userLocation={userLocation}
          setUserLocation={setUserLocation}
        />
      );
    default:
      return null;
  }
}
